import { useFocusRing } from '@react-native-aria/focus'
import { mergeProps } from '@react-native-aria/utils'
import clsx from 'clsx'
import { Link, useRouter } from 'expo-router'
import type { LinkProps } from 'expo-router/build/link/Link'
import type { ReactNode, Ref } from 'react'
import { Platform, Pressable, type View } from 'react-native'

import type { ICON } from '@/src/general/enums/icons'
import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { Icon, type TIconProps } from '../media/Icon'
import { listboxItemVariants, type TListboxItemSlots, type TListboxItemVariantProps } from './listboxItemVariants'

export type TListboxItemProps = Partial<LinkProps> & {
  /**
   * Ref to the DOM node.
   */
  ref?: Ref<View>
  /**
   * The listbox item start content.
   */
  startContent?: ReactNode
  /**
   * The listbox icon. Will be overwritten by `startContent` if passed.
   */
  icon?: ICON
  iconProps?: Partial<TIconProps>
  /**
   * The listbox item end content.
   */
  endContent?: ReactNode
  /**
   * Classname or List of classes to change the classNames of the element.
   * if `className` is passed, it will be added to the base slot.
   *
   * @example
   * ```ts
   * <ListboxItem classNames={{
   *    base:"base-classes",
   *    label:"label-classes",
   * }} />
   * ```
   */
  classNames?: SlotsToClasses<TListboxItemSlots>
  tabIndex?: number
} & TListboxItemVariantProps

export const useListboxItem = (originalProps: TListboxItemProps) => {
  const { push } = useRouter()
  const [props, variantProps] = mapPropsVariants(originalProps, listboxItemVariants.variantKeys)

  const { bold, isDisabled } = originalProps

  const { icon, iconProps, href, startContent, endContent, children, className, classNames, ...otherProps } = props

  const isLink = !!href && !isDisabled
  const isWebLink = isLink && Platform.OS === 'web'
  const Component = isWebLink ? Link : Pressable

  const { isFocusVisible, focusProps } = useFocusRing()

  const slots = listboxItemVariants({ ...variantProps, isDisabled })

  const mergedItemProps = mergeProps(
    otherProps,
    focusProps,
    isLink && !isWebLink && href
      ? {
          onPress: () => push(href),
        }
      : {},
  )

  const getItemProps = () => ({
    ...(isWebLink ? { href } : {}),
    dataSet: {
      disabled: isDisabled,
      'focus-visible': isFocusVisible,
    },
    className: slots.base({ class: clsx(classNames?.base, className) }),
    ...(mergedItemProps as object),
  })

  const getLabelProps = () => ({
    bold,
    dataSet: {
      label: true,
    },
    className: slots.label({ class: classNames?.label }),
    ...(Platform.OS === 'android'
      ? { style: { includeFontPadding: false, textAlignVertical: 'center' as const } }
      : {}),
  })

  const getIconProps = (name: ICON) => ({
    name,
    size: bold ? ('sm' as TBreakpoint) : ('xs' as TBreakpoint),
    ...iconProps,
    className: slots.icon({ class: clsx(classNames?.icon, iconProps?.className) }),
  })

  return {
    Component,
    children,
    startContent: startContent || (icon && <Icon {...getIconProps(icon)} />),
    endContent,
    getItemProps,
    getLabelProps,
  }
}
